import _ from 'lodash';
import { asyncRequestContextsSelectors } from '@lifeomic/phc-web-toolkit/src/redux/modules/asyncRequestContexts';
import filesActionTypes from './actionTypes';
import filesPageActions from '../pages/filesPage/actions';
import NotificationHelper from '@lifeomic/phc-web-toolkit/src/redux/util/NotificationHelper';
import queryString from 'query-string';
import urls from '../../../util/urls';
import { projectCohortPickerSelectors } from '@lifeomic/phc-web-toolkit/src/redux/modules/projectCohortPicker';
import { projectPickerSelectors } from '@lifeomic/phc-web-toolkit/src/redux/modules/projectPicker';

const { selectContexts, selectKeyedContext } = asyncRequestContextsSelectors;

function getAsyncRequestContextProps(contextKey: any) {
  if (!contextKey) return null;

  return {
    contextKey,
    parseResponseItems: (data: any) => _.get(data, 'items'),
    parseNextToken: (data: any) => _.get(data, 'links.next'),
  };
}

function getCohortRestrictionState(state: any) {
  const cohort =
    projectCohortPickerSelectors.selectActiveProjectCohortId(state);
  if (!cohort) return null;
  const project = projectPickerSelectors.selectActiveProjectId(state);

  return { cohort, project };
}

/** @deprecated use fetchProjectFiles */
function fetchFiles(
  { params, next }: { params?: Record<string, unknown>; next?: string } = {},
  contextKey: string,
) {
  return async (dispatch: any) => {
    let nextUrl;
    if (next) {
      const parts = next.split('?');
      if (parts.length === 1)
        return dispatch(fetchFilesError(new Error('Invalid `next` parameter')));
      nextUrl = urls.api.files.files({
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        ...queryString.parse(parts.pop()),
        ...params,
      });
    }

    await dispatch({
      type: filesActionTypes.FETCH_FILES,
      url: nextUrl || urls.api.files.files(params),
      responseContext: getAsyncRequestContextProps(contextKey),
    });
  };
}

function fetchProjectFiles(
  {
    id,
    params,
    next,
  }: { id?: string; params?: Record<string, unknown>; next?: string } = {},
  contextKey: string,
) {
  return async (dispatch: any, getState: any) => {
    let nextUrl;
    const cohortRestrictions = getCohortRestrictionState(getState());

    let nextValidatedParams = params;
    if (next) {
      const parts = next.split('?');
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      nextValidatedParams = { ...queryString.parse(parts.pop()), ...params };
    }

    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    let url = urls.api.projects.files(nextValidatedParams, id);
    if (cohortRestrictions) {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      url = urls.api.files.searchPatientFiles(cohortRestrictions.project, {
        ...(nextValidatedParams || {}),
        cohort: cohortRestrictions.cohort,
      });
    }

    await dispatch({
      type: filesActionTypes.FETCH_PROJECT_FILES,
      url: nextUrl || url,
      responseContext: getAsyncRequestContextProps(contextKey),
    });

    if (!contextKey) return;

    const contexts = selectContexts(getState());
    const { request, responses } = selectKeyedContext(contexts, contextKey);

    if (request && request.error) {
      return dispatch(fetchFilesError(new Error('Error fetching files')));
    }

    if (next) {
      await dispatch(filesPageActions.listPush(responses));
      return;
    }

    await dispatch(filesPageActions.listReplace(responses));
  };
}

function deleteFile(
  fileId: any,
  contextKey: any,
  notification = new NotificationHelper(),
) {
  return async (dispatch: any) => {
    await dispatch({
      type: filesActionTypes.DELETE_FILE,
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      url: urls.api.files.files(null, fileId),
      fetchOptions: {
        method: 'DELETE',
      },
      success: () => {
        return deleteFileSuccess(fileId);
      },
      responseContext: getAsyncRequestContextProps(contextKey),
      ...notification.getAsyncRequestHandlers(),
    });
  };
}

function getDownloadUrlContextProps(contextKey: any) {
  if (!contextKey) return null;

  return {
    contextKey,
    parseResponseItems: (data: any) => data,
  };
}

function fetchDownloadUrl(
  fileId: any,
  includeContentDisposition = true,
  contextKey: any,
  notification = new NotificationHelper(),
) {
  const params = {
    include: 'downloadUrl',
    includeContentDisposition,
  };

  return async (dispatch: any, getState: any) => {
    const cohortRestrictions = getCohortRestrictionState(getState());
    let url = urls.api.files.files(params, fileId);

    if (cohortRestrictions) {
      url = urls.api.files.patientFile(
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        cohortRestrictions.project,
        { ...params, cohort: cohortRestrictions.cohort },
        fileId,
      );
    }

    await dispatch({
      type: filesActionTypes.FETCH_FILE_DOWNLOAD_URL,
      url,
      responseContext: getDownloadUrlContextProps(contextKey),
      ...notification.getAsyncRequestHandlers(),
    });
  };
}

const getGetFileContextProps = (contextKey: any) => {
  if (!contextKey) return null;

  return {
    contextKey,
    parseResponseItems: (data: any) => {
      const encoder = new TextEncoder();
      const pdfData = encoder.encode(data);
      return pdfData;
    },
  };
};

const getFile = (url: any, contextKey: any) => {
  return async (dispatch: any) => {
    await dispatch({
      type: filesActionTypes.GET_FILE,
      excludeAuth: true,
      url,
      responseContext: getGetFileContextProps(contextKey),
    });
  };
};

function createProjectFile(
  datasetId: any,
  file: any,
  notification = new NotificationHelper(),
) {
  return async (dispatch: any, getState: any) => {
    await dispatch({
      type: filesActionTypes.CREATE_FILE,
      url: urls.api.files.files(),
      fetchOptions: {
        method: 'POST',
        body: JSON.stringify({
          name: file.name,
          datasetId,
          contentType: file.type,
        }),
      },
      onFailure: notification.getAsyncRequestFailureHandler(),
    });

    const state = getState();
    if (!state.files.create.error && state.files.create.response) {
      const urlResp = state.files.create.response;
      await dispatch({
        type: filesActionTypes.UPLOAD_FILE,
        url: urlResp.uploadUrl,
        excludeAuth: true,
        fetchOptions: {
          method: 'PUT',
          headers: {
            'Content-Length': file.size,
            'Content-Type': file.type,
          },
          body: file,
        },
        ...notification.getAsyncRequestHandlers(),
      });

      // Add `filename.extension` to the files page list so it appears
      const addedFile = {
        ...urlResp,
        type: 'FILE',
        name: file.name.substr(file.name.lastIndexOf('/') + 1),
      };

      await dispatch(filesPageActions.listPush([addedFile]));
    }
  };
}

function deleteFileSuccess(deletedFileId: any) {
  return {
    type: filesActionTypes.DELETE_FILE_SUCCESS,
    response: deletedFileId,
  };
}

function fetchFilesError(error: any) {
  return {
    type: filesActionTypes.FETCH_FILES_ERROR,
    error,
  };
}

function renameProjectFile(
  fileId: any,
  name: any,
  oldName: any,
  contextKey: any,
  notification = new NotificationHelper(),
) {
  return async (dispatch: any, getState: any) => {
    await dispatch({
      type: filesActionTypes.RENAME_FILE,
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      url: urls.api.files.files(null, fileId),
      fetchOptions: {
        method: 'PATCH',
        body: JSON.stringify({
          name: name,
        }),
      },
      responseContext: getAsyncRequestContextProps(contextKey),
      ...notification.getAsyncRequestHandlers(),
    });

    const contexts = selectContexts(getState());
    const {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      request: { error, response },
    } = selectKeyedContext(contexts, contextKey);

    if (error) {
      return;
    }

    // If the file is being moved from the current directory to a new directory,
    // then we should just remove it from the list (to simulate the file
    // being moved to the user).
    if (
      oldName &&
      name.substr(0, name.lastIndexOf('/')) !==
        oldName.substr(0, oldName.lastIndexOf('/'))
    ) {
      await dispatch(filesPageActions.deleteFromFileList(fileId));
      return;
    }

    // Since we are in a directory, remove the paths and
    // set name to `filename.extension` so it appears in
    // the list correctly
    const renamedFile = {
      ...(response as any),
      type: 'FILE',
      name: name.substr(name.lastIndexOf('/') + 1),
    };

    await dispatch(filesPageActions.changeFileInList(renamedFile));
  };
}

function setActivePath(activePath: any) {
  return (dispatch: any) =>
    dispatch({
      type: filesActionTypes.SET_ACTIVE_FILES_PATH,
      activePath,
    });
}

export default {
  fetchFiles,
  fetchProjectFiles,
  fetchFilesError,
  deleteFile,
  fetchDownloadUrl,
  getFile,
  createProjectFile,
  renameProjectFile,
  setActivePath,
};
